import { useContext, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { UniversalWorklistQuery } from './UniversalWorklistQuery';
import { WorklistItem } from './types/workListItem';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';
import { UserContext } from '../../contexts/UserContext';
import {
  DevHubAlert,
  DevHubBox,
  DevHubButton,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { Progress } from '@backstage/core-components';
import { WorkBoxItem } from './WorkboxItem';
import { GoogleClickId } from '../../util/constants';
import { useNavigate } from 'react-router-dom';

/**
 * Component that displays a user's worklist items with caching and refetching capabilities.
 * It fetches worklist items from a GraphQL query, caches them in local storage for 12 hours,
 * @param props - The component props
 * @param props.twoColumns - Whether to use a two-column layout (true) or default responsive grid (false)
 * @param props.showDescriptions - Whether to show item descriptions (true) or hide them (false)
 * @constructor
 */
export const MyUniversalWorklist = ({
  twoColumns = false,
  showDescriptions = true,
}: {
  twoColumns?: boolean;
  showDescriptions?: boolean;
}) => {
  const navigate = useNavigate();
  const { login } = useContext(UserContext);
  const CACHE_KEY = 'worklistItems';
  const CACHE_DURATION = 12 * 60 * 60 * 1000; // 12 hours in milliseconds
  const [isRefetching, setIsRefetching] = useState(false);
  const [cachedData, setCachedData] = useState<WorklistItem[]>([]);
  const uwlEnabled = ConfigHelper.instance.isFeatureEnabled(
    Feature.UNIVERSAL_WORKLIST,
  );

  const { data, loading, refetch } = useQuery(UniversalWorklistQuery, {
    variables: { filter: { login: login, statuses: ['OPEN', 'IN_PROGRESS'] } },
    skip: Boolean(cachedData.length), // Skip the query if there is cached data
  });

  const worklistItems: WorklistItem[] = cachedData.length
    ? cachedData
    : data?.devhub?.universalWorklist.worklistItemsPaged?.worklistItems || [];

  const statusColors = (status: string) => {
    if (status === 'CLOSED') {
      return 'green';
    } else if (status === 'IN_PROGRESS') {
      return 'blueGreen';
    } else if (status === 'OPEN') {
      return 'blue';
    }
    return 'red';
  };

  useEffect(() => {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const { timestamp, items } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        setCachedData(items);
      } else {
        localStorage.removeItem(CACHE_KEY);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data && !cachedData.length) {
      const fetchedItems =
        data?.devhub?.universalWorklist.worklistItemsPaged?.worklistItems || [];

      const mappedItems = fetchedItems.map((item: any) => ({
        actionUrl: item.actionUrl,
        category: item.category,
        description: item.description,
        dueDate: new Date(item.dueDate),
        id: item.id,
        name: item.name,
        priority: item.priority,
        source: item.source,
        status: item.status.replace(/_/g, ' '),
        supportUrl: item.supportUrl,
      }));

      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ timestamp: Date.now(), items: mappedItems }),
      );

      setCachedData(mappedItems);
    }
  }, [data, cachedData.length]);

  if (!uwlEnabled) {
    return (
      <DevHubAlert severity="info">
        Universal Worklist feature is not enabled.
      </DevHubAlert>
    );
  }

  const handleViewAll = (component: string) => {
    navigate(`/action-center/${component}`);
  };

  const handleRefresh = async () => {
    setIsRefetching(true);
    await refetch();
    setIsRefetching(false);
    localStorage.removeItem(CACHE_KEY);
  };

  return (
    <>
      {uwlEnabled && (
        <>
          {(loading || isRefetching) && <Progress />}
          <DevHubBox display="inline-flex" alignItems="center">
            <DevHubTypography>My Worklist</DevHubTypography>
            <DevHubButton
              buttonColor="themeBased"
              onClick={() => handleViewAll('worklist')}
            >
              <DevHubTypography fontColor="blue" fontSize="small">
                View All
              </DevHubTypography>
            </DevHubButton>
            <DevHubBox tbPadding="small">
              <DevHubButton onClick={handleRefresh}>
                <DevHubDynamicIcon fontSize="medium" iconName="Sync" />
              </DevHubButton>
            </DevHubBox>
          </DevHubBox>

          {!loading && worklistItems.length === 0 && (
            <DevHubAlert severity="info">No worklist items found.</DevHubAlert>
          )}
          <DevHubGrid container spacing={2}>
            {worklistItems.slice(0, 6).map((item, index) => (
              <DevHubGrid
                item
                xs={12}
                md={twoColumns ? 6 : 4}
                lg={twoColumns ? 6 : 4}
                key={item.id}
              >
                <WorkBoxItem
                  badgeNumber={index + 1}
                  chipLabel={item.status}
                  chipColor={statusColors(item.status)}
                  link={item.actionUrl}
                  title={item.name}
                  dueDate={item.dueDate}
                  body={showDescriptions ? item.description : null}
                  id={`${GoogleClickId.WORKLISTITEM}-${item.id}`}
                />
              </DevHubGrid>
            ))}
          </DevHubGrid>
        </>
      )}
    </>
  );
};
